/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Typography, Fab } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import moment from 'moment-timezone'
import { forceConsultationsPageTab } from '../../../redux/actions'

import Animated from '../../common/animated/Animated'
import { useAppDispatch, useAppSelector } from '../../../store'
import useStyles from './styles/PrimaryHeaderLargeNotificationsStyles'
import { UserLiveQuery } from '../../../utils/LiveQuery/UserLiveQuery'
import { LiveQuery } from '../../../utils/LiveQuery/LiveQuery'
import { CloseIcon, ConsultationIcon, TransmissionIcon } from '../../../../assets/icons'
import { useQueryClient } from '@tanstack/react-query'
import { CONSULTATION_KEYS, noDisplayProtectedUrls, noRedirectProtectedUrls, TRANSMISSION_KEYS } from './_utils'

const PrimaryHeaderLargeNotifications = () => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { user } = useAppSelector((state) => state.auth)
    const userObjectId = user?.objectId
    const [notifications, setNotifications] = useState<Array<any>>([])
    const consultationType = 'Consultation'
    const transmissionType = 'Transmission'
    const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | undefined>(undefined)
    const [protectedUrlType, setProtectedUrlType] = useState('')
    const timeoutDelay = protectedUrlType !== 'noredirect' ? 8000 : 3000

    const queryClient = useQueryClient()

    const invalidate = (keys: string[]) => {
        keys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key, userObjectId] })
        })
    }

    const refetchConsultations = useCallback(() => {
        invalidate(CONSULTATION_KEYS)
    }, [queryClient, userObjectId])

    const refetchTransmissions = useCallback(() => {
        invalidate(TRANSMISSION_KEYS)
    }, [queryClient, userObjectId])

    useEffect(() => {
        let subscriptions: LiveQuery[] = []
        const initLiveQuery = async () => {
            const subTLCPending = new UserLiveQuery(userObjectId, (consultation: any) => {
                refetchConsultations()
                const consultationInfos = consultation.toJSON()
                consultationInfos.typename = consultation.className
                if (notifications.map((item) => item.objectId).indexOf(consultationInfos.objectId) === -1)
                    setNotifications([consultationInfos, ...notifications])
            })
            await subTLCPending.init()
            await subTLCPending.onPendingConsultationCreateOrEnter()

            const subTLCPendingLeave = new UserLiveQuery(userObjectId, () => {
                refetchConsultations()
            })
            await subTLCPendingLeave.init()
            await subTLCPendingLeave.onPendingConsultationLeave()

            const subTLEPendingLeave = new UserLiveQuery(userObjectId, () => {
                refetchTransmissions()
            })
            await subTLEPendingLeave.init()
            await subTLEPendingLeave.onTransmitedTransmissionLeave()

            const subTLEPending = new UserLiveQuery(userObjectId, () => {
                refetchTransmissions()
            })
            await subTLEPending.init()
            await subTLEPending.onPendingTransmissionCreateOrEnter()

            subscriptions = [subTLCPending, subTLCPendingLeave, subTLEPendingLeave, subTLEPending]
        }
        if (userObjectId) initLiveQuery()

        return () => {
            subscriptions.forEach((sub) => {
                sub.unsubscribe()
            })
        }
    }, [userObjectId, notifications, refetchConsultations, refetchTransmissions])

    useEffect(() => {
        const isNoRedirectProtectedUrls = noRedirectProtectedUrls.filter((path) => path === location.pathname)
        const isNoDisplayProtectedUrls = noDisplayProtectedUrls.filter((path) => path === location.pathname)

        if (isNoRedirectProtectedUrls.length > 0) {
            setProtectedUrlType('noredirect')
        } else if (isNoDisplayProtectedUrls.length > 0) {
            setProtectedUrlType('nodisplay')
        } else {
            setProtectedUrlType('')
        }
    }, [location])

    const clearNotifications = () => {
        setNotifications([])
        if (timeoutId) clearTimeout(timeoutId)
    }

    useEffect(() => {
        if (timeoutId) clearTimeout(timeoutId)

        if (notifications.length > 0) {
            const newTimeoutId = setTimeout(() => {
                clearNotifications()
            }, timeoutDelay)

            setTimeoutId(newTimeoutId)
        }
    }, [notifications])

    const handleNotificationClose = (actType: any) => {
        if (protectedUrlType !== 'noredirect') {
            if (actType === consultationType) {
                navigate('/consultations')
                dispatch(
                    forceConsultationsPageTab({
                        pending: true,
                        started: false,
                    }),
                )
            } else if (actType === transmissionType) {
                navigate('/transmissions')
            }
        }
        clearNotifications()
    }

    const getNotificationReactNodes = (): React.ReactNode[] =>
        notifications.length > 0
            ? notifications.map((notification: any, index: any) => {
                  const actType = notification.typename === consultationType ? 'téléconsultation' : 'téléexpertise'
                  const readableMessage = `Une ${actType} vient d'arriver`

                  const isLastOrPreLastItem = [notifications.length - 1, notifications.length - 2].includes(index)

                  return (
                      <div
                          key={notification.actId}
                          className={clsx(
                              classes.PrimaryHeaderLargeNotification,
                              !isLastOrPreLastItem && classes.PrimaryHeaderLargeNotificationHidden,
                              notification.typename === consultationType
                                  ? classes.PrimaryHeaderLargeNotificationConsultation
                                  : classes.PrimaryHeaderLargeNotificationTransmission,
                          )}
                      >
                          <div
                              className={classes.PrimaryHeaderLargeNotificationContent}
                              onClick={() => handleNotificationClose(notification.typename)}
                          >
                              <div className={classes.PrimaryHeaderLargeNotificationIcon}>
                                  {notification.typename === consultationType && <ConsultationIcon />}
                                  {notification.typename === transmissionType && <TransmissionIcon />}
                              </div>
                              <div className={classes.PrimaryHeaderLargeNotificationContentInner}>
                                  <div>
                                      <Typography className={classes.PrimaryHeaderLargeNotificationContentText}>
                                          {readableMessage}
                                      </Typography>
                                      <Typography className={classes.PrimaryHeaderLargeNotificationContentDate}>
                                          {moment(notification.appointmentDate.iso).format('DD/MM/YYYY | HH:mm:ss')}
                                      </Typography>
                                  </div>
                                  <div className={classes.PrimaryHeaderLargeNotificationContentButtonContainer}>
                                      {protectedUrlType !== 'noredirect' && (
                                          <Button
                                              variant="contained"
                                              className={classes.PrimaryHeaderLargeNotificationContentButton}
                                              disableElevation
                                              onClick={() => handleNotificationClose(notification.typename)}
                                          >
                                              Voir
                                          </Button>
                                      )}
                                  </div>
                              </div>
                          </div>
                          <Fab
                              // @ts-expect-error TS(2769) FIXME: No overload matches this call.
                              variant="contained"
                              className={clsx(
                                  classes.PrimaryHeaderLargeNotificationContentCloseButton,
                                  'PrimaryHeaderLargeNotificationContentCloseButton',
                              )}
                              onClick={clearNotifications}
                          >
                              <CloseIcon />
                          </Fab>
                      </div>
                  )
              })
            : []

    if (protectedUrlType === 'nodisplay') return null

    return (
        <div
            className={clsx(
                classes.PrimaryHeaderLargeNotifications,
                notifications.length === 0 && classes.PrimaryHeaderLargeNotificationsHidden,
            )}
        >
            <Animated transitionName="large-notification" enter exit items={getNotificationReactNodes()} />
        </div>
    )
}

export default PrimaryHeaderLargeNotifications
